/**
 * UTM Campaign Tracker
 * Captures and persists UTM parameters throughout user session
 * Supports: Meta, TikTok, Google Ads campaigns
 */

class UTMTracker {
  constructor() {
    this.STORAGE_KEY = 'campaign_utm_data';
    this.TOKEN_KEY = 'campaign_access_token';
    this.EXPIRY_DAYS = 30; // UTM data expires after 30 days
    
    // YOUR VALID ACCESS TOKENS
    this.VALID_TOKENS = [
      'eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imt1enFid3ZtYmVoYXNtY292dW9kIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTk3MzExOTUsImV4cCI6MjA3NTMwNzE5NX0',
      // Add more tokens here as you create campaigns
      // 'eyJjYW1wYWlnbiI6Im1ldGFfc3VtbWVyIiwiZXhwaXJlcyI6IjIwMjUtMTItMzEifQ',
    ];
    
    this.init();
  }

  init() {
    // Capture UTM parameters on page load
    const utmParams = this.getUTMFromURL();
    
    // Capture and validate access token
    const accessToken = this.getAccessTokenFromURL();
    
    if (Object.keys(utmParams).length > 0) {
      // New campaign detected - save it
      this.saveUTMData(utmParams);
      console.log('📊 UTM Campaign Captured:', utmParams);
    }
    
    if (accessToken) {
      // Validate and save access token
      if (this.validateAccessToken(accessToken)) {
        this.saveAccessToken(accessToken);
        console.log('🔑 Access Token Validated');
      } else {
        console.warn('❌ Invalid Access Token');
      }
    }

    // Check if user has full access (UTM + Token)
    this.checkFullAccess();
    
    // Always display current campaign data
    this.displayCampaignInfo();
    
    // Attach to all forms (for cart, checkout, etc.)
    this.attachToForms();
  }

  /**
   * Get access token from URL
   */
  getAccessTokenFromURL() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('token') || urlParams.get('access_token') || null;
  }

  /**
   * Validate access token against whitelist
   */
  validateAccessToken(token) {
    return this.VALID_TOKENS.includes(token);
  }

  /**
   * Save access token to storage
   */
  saveAccessToken(token) {
    const data = {
      token: token,
      expires: new Date().getTime() + (this.EXPIRY_DAYS * 24 * 60 * 60 * 1000),
      verified_at: new Date().toISOString()
    };
    
    try {
      localStorage.setItem(this.TOKEN_KEY, JSON.stringify(data));
      sessionStorage.setItem(this.TOKEN_KEY, JSON.stringify(data));
      
      window.dispatchEvent(new CustomEvent('token:verified', { detail: data }));
    } catch (e) {
      console.error('Failed to save access token:', e);
    }
  }

  /**
   * Get stored access token (from sessionStorage, localStorage, OR cookies)
   */
  getStoredAccessToken() {
    try {
      let data = sessionStorage.getItem(this.TOKEN_KEY);
      
      if (!data) {
        data = localStorage.getItem(this.TOKEN_KEY);
      }
      
      // Fall back to cookies (for returning visitors)
      if (!data) {
        data = this.getFromCookie('campaign_token');
        
        // If found in cookie, restore to sessionStorage
        if (data) {
          sessionStorage.setItem(this.TOKEN_KEY, data);
        }
      }
      
      if (!data) return null;
      
      const parsed = JSON.parse(data);
      
      // Check if expired
      if (parsed.expires && new Date().getTime() > parsed.expires) {
        this.clearAccessToken();
        return null;
      }
      
      return parsed.token;
    } catch (e) {
      console.error('Failed to read access token:', e);
      return null;
    }
  }

  /**
   * Clear stored access token
   */
  clearAccessToken() {
    localStorage.removeItem(this.TOKEN_KEY);
    sessionStorage.removeItem(this.TOKEN_KEY);
  }

  /**
   * Check if user has FULL ACCESS (UTM + Valid Token)
   */
  checkFullAccess() {
    const hasUTM = this.getStoredUTMData() !== null;
    const hasValidToken = this.getStoredAccessToken() !== null;
    const hasFullAccess = hasUTM && hasValidToken;
    
    // Store access status
    this.fullAccess = hasFullAccess;
    
    // Dispatch event for other scripts to listen
    window.dispatchEvent(new CustomEvent('campaign:access', { 
      detail: { 
        hasFullAccess,
        hasUTM,
        hasToken: hasValidToken
      } 
    }));
    
    console.log('🔐 Access Status:', {
      hasUTM,
      hasToken: hasValidToken,
      fullAccess: hasFullAccess
    });
    
    return hasFullAccess;
  }

  /**
   * Check if user has full access (UTM + Token)
   */
  hasFullAccess() {
    return this.checkFullAccess();
  }

  /**
   * Extract UTM parameters from current URL
   */
  getUTMFromURL() {
    const urlParams = new URLSearchParams(window.location.search);
    const utmParams = {};
    
    // Custom campaign token (YOUR controlled parameter)
    const campaignToken = urlParams.get('campaign') || urlParams.get('c');
    if (campaignToken) {
      utmParams.campaign_token = campaignToken;
      utmParams.campaign_verified = this.verifyCampaignToken(campaignToken);
    }
    
    // Standard UTM parameters
    const utmKeys = [
      'utm_source',      // e.g., facebook, google, tiktok
      'utm_medium',      // e.g., cpc, social, email
      'utm_campaign',    // e.g., summer_sale_2024
      'utm_term',        // e.g., luxury_perfume
      'utm_content',     // e.g., ad_variant_a
      'fbclid',          // Facebook Click ID
      'gclid',           // Google Click ID
      'ttclid',          // TikTok Click ID
    ];
    
    utmKeys.forEach(key => {
      const value = urlParams.get(key);
      if (value) {
        utmParams[key] = value;
      }
    });
    
    // Add timestamp and landing page
    if (Object.keys(utmParams).length > 0) {
      utmParams.timestamp = new Date().toISOString();
      utmParams.landing_page = window.location.pathname;
      utmParams.referrer = document.referrer || 'direct';
    }
    
    return utmParams;
  }

  /**
   * Verify custom campaign token against your whitelist
   * Add your valid campaign tokens here
   */
  verifyCampaignToken(token) {
    // YOUR CUSTOM CAMPAIGN TOKENS
    const validTokens = {
      // Meta/Facebook Campaigns
      'META_SUMMER2024': { platform: 'meta', campaign: 'Summer Sale 2024' },
      'META_BLACKFRIDAY': { platform: 'meta', campaign: 'Black Friday 2024' },
      'FB_LUXURY_Q4': { platform: 'meta', campaign: 'Q4 Luxury Push' },
      
      // TikTok Campaigns
      'TIKTOK_VIRAL_NOV': { platform: 'tiktok', campaign: 'November Viral Campaign' },
      'TT_INFLUENCER_2024': { platform: 'tiktok', campaign: 'Influencer Partnership' },
      'TIKTOK_PERFUME_TRENDS': { platform: 'tiktok', campaign: 'Perfume Trends' },
      
      // Google Ads Campaigns
      'GOOGLE_SEARCH_PREMIUM': { platform: 'google', campaign: 'Premium Search Ads' },
      'GGL_SHOPPING_EU': { platform: 'google', campaign: 'EU Shopping Ads' },
      'GOOGLE_DISPLAY_RETARGET': { platform: 'google', campaign: 'Display Retargeting' },
      
      // Email Campaigns
      'EMAIL_WELCOME_SERIES': { platform: 'email', campaign: 'Welcome Series' },
      'EMAIL_VIP_EXCLUSIVE': { platform: 'email', campaign: 'VIP Exclusive Offer' },
      
      // Affiliate/Partner Campaigns
      'PARTNER_INFLUENCER_001': { platform: 'affiliate', campaign: 'Influencer Partner 001' },
      'AFFILIATE_BEAUTY_BLOG': { platform: 'affiliate', campaign: 'Beauty Blog Network' },
    };
    
    return validTokens[token.toUpperCase()] || null;
  }

  /**
   * Save UTM data to localStorage, sessionStorage, AND cookies with expiry
   */
  saveUTMData(utmParams) {
    const data = {
      utm: utmParams,
      expires: new Date().getTime() + (this.EXPIRY_DAYS * 24 * 60 * 60 * 1000)
    };
    
    try {
      // Save to localStorage
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
      
      // Save to sessionStorage for current session
      sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
      
      // ALSO save to cookies for cross-session persistence
      this.saveToCookie('campaign_data', JSON.stringify(data), this.EXPIRY_DAYS);
      
      // Dispatch custom event for tracking
      window.dispatchEvent(new CustomEvent('utm:captured', { detail: utmParams }));
    } catch (e) {
      console.error('Failed to save UTM data:', e);
    }
  }

  /**
   * Save access token to storage AND cookies
   */
  saveAccessToken(token) {
    const data = {
      token: token,
      expires: new Date().getTime() + (this.EXPIRY_DAYS * 24 * 60 * 60 * 1000),
      verified_at: new Date().toISOString()
    };
    
    try {
      localStorage.setItem(this.TOKEN_KEY, JSON.stringify(data));
      sessionStorage.setItem(this.TOKEN_KEY, JSON.stringify(data));
      
      // ALSO save to cookies for returning visitors
      this.saveToCookie('campaign_token', JSON.stringify(data), this.EXPIRY_DAYS);
      
      window.dispatchEvent(new CustomEvent('token:verified', { detail: data }));
    } catch (e) {
      console.error('Failed to save access token:', e);
    }
  }

  /**
   * Save data to cookies
   */
  saveToCookie(name, value, days) {
    const expires = new Date();
    expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
    
    // Secure cookie settings
    const cookieOptions = [
      `${name}=${encodeURIComponent(value)}`,
      `expires=${expires.toUTCString()}`,
      'path=/',
      'SameSite=Lax',
      // Add Secure flag if HTTPS
      window.location.protocol === 'https:' ? 'Secure' : ''
    ].filter(Boolean).join('; ');
    
    document.cookie = cookieOptions;
  }

  /**
   * Get data from cookies
   */
  getFromCookie(name) {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    
    for(let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) {
        return decodeURIComponent(c.substring(nameEQ.length, c.length));
      }
    }
    return null;
  }

  /**
   * Delete cookie
   */
  deleteCookie(name) {
    document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  }

  /**
   * Get stored UTM data (from sessionStorage, localStorage, OR cookies)
   */
  getStoredUTMData() {
    try {
      // Try sessionStorage first (current session)
      let data = sessionStorage.getItem(this.STORAGE_KEY);
      
      // Fall back to localStorage
      if (!data) {
        data = localStorage.getItem(this.STORAGE_KEY);
      }
      
      // Fall back to cookies (for returning visitors)
      if (!data) {
        data = this.getFromCookie('campaign_data');
        
        // If found in cookie, restore to sessionStorage for faster access
        if (data) {
          sessionStorage.setItem(this.STORAGE_KEY, data);
        }
      }
      
      if (!data) return null;
      
      const parsed = JSON.parse(data);
      
      // Check if expired
      if (parsed.expires && new Date().getTime() > parsed.expires) {
        this.clearUTMData();
        return null;
      }
      
      return parsed.utm;
    } catch (e) {
      console.error('Failed to read UTM data:', e);
      return null;
    }
  }

  /**
   * Clear stored UTM data (from all storage)
   */
  clearUTMData() {
    localStorage.removeItem(this.STORAGE_KEY);
    sessionStorage.removeItem(this.STORAGE_KEY);
    this.deleteCookie('campaign_data');
  }

  /**
   * Clear stored access token (from all storage)
   */
  clearAccessToken() {
    localStorage.removeItem(this.TOKEN_KEY);
    sessionStorage.removeItem(this.TOKEN_KEY);
    this.deleteCookie('campaign_token');
  }

  /**
   * Display campaign info in console/UI
   */
  displayCampaignInfo() {
    const utm = this.getStoredUTMData();
    
    if (!utm) {
      console.log('📊 No active campaign');
      return;
    }
    
    console.log('📊 Active Campaign:', {
      source: utm.utm_source || 'N/A',
      medium: utm.utm_medium || 'N/A',
      campaign: utm.utm_campaign || 'N/A',
      since: utm.timestamp
    });
    
    // Optionally display in UI (for testing/admin)
    if (window.location.search.includes('debug_utm=1')) {
      this.showDebugBanner(utm);
    }
  }

  /**
   * Show debug banner (only when ?debug_utm=1 in URL)
   */
  showDebugBanner(utm) {
    const banner = document.createElement('div');
    banner.style.cssText = `
      position: fixed;
      bottom: 20px;
      right: 20px;
      background: #000;
      color: #0f0;
      padding: 15px;
      border-radius: 8px;
      font-family: monospace;
      font-size: 12px;
      z-index: 9999;
      max-width: 300px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.3);
    `;
    
    banner.innerHTML = `
      <strong>🎯 Campaign Active</strong><br>
      Source: ${utm.utm_source || 'N/A'}<br>
      Medium: ${utm.utm_medium || 'N/A'}<br>
      Campaign: ${utm.utm_campaign || 'N/A'}<br>
      <small>Since: ${new Date(utm.timestamp).toLocaleString()}</small>
    `;
    
    document.body.appendChild(banner);
    
    // Auto-hide after 5 seconds
    setTimeout(() => banner.remove(), 5000);
  }

  /**
   * Attach UTM data to all forms (cart, checkout, etc.)
   */
  attachToForms() {
    const utm = this.getStoredUTMData();
    if (!utm) return;
    
    // Add hidden inputs to forms when they're submitted
    document.addEventListener('submit', (e) => {
      const form = e.target;
      
      // Skip if not a form or already has UTM data
      if (!form || form.querySelector('[name="utm_source"]')) return;
      
      // Add UTM data as hidden fields
      Object.entries(utm).forEach(([key, value]) => {
        if (key.startsWith('utm_') || key === 'fbclid' || key === 'gclid' || key === 'ttclid') {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = key;
          input.value = value;
          form.appendChild(input);
        }
      });
    });
    
    // Attach to cart/checkout URLs
    this.enhanceCheckoutLinks();
  }

  /**
   * Add UTM parameters to checkout and cart links
   */
  enhanceCheckoutLinks() {
    const utm = this.getStoredUTMData();
    if (!utm) return;
    
    // Find all checkout/cart links
    const checkoutLinks = document.querySelectorAll('a[href*="/checkout"], a[href*="/cart"], [name="checkout"], button[name="checkout"]');
    
    checkoutLinks.forEach(link => {
      link.addEventListener('click', () => {
        // Store UTM in session for checkout
        try {
          sessionStorage.setItem('checkout_utm', JSON.stringify(utm));
        } catch (e) {
          console.error('Failed to store checkout UTM:', e);
        }
      });
    });
  }

  /**
   * Get campaign attribution summary
   */
  getCampaignAttribution() {
    const utm = this.getStoredUTMData();
    
    if (!utm) {
      return {
        hasActiveCampaign: false,
        source: 'direct',
        medium: 'none',
        campaign: null
      };
    }
    
    return {
      hasActiveCampaign: true,
      source: utm.utm_source || 'unknown',
      medium: utm.utm_medium || 'unknown',
      campaign: utm.utm_campaign || null,
      content: utm.utm_content || null,
      term: utm.utm_term || null,
      clickId: utm.fbclid || utm.gclid || utm.ttclid || null,
      landingPage: utm.landing_page,
      timestamp: utm.timestamp
    };
  }

  /**
   * Check if user came from specific campaign using YOUR custom token
   */
  isFromCampaign(tokenOrSource = null, campaign = null) {
    const utm = this.getStoredUTMData();
    if (!utm) return false;
    
    // Check by custom campaign token (YOUR tokens)
    if (tokenOrSource && utm.campaign_token) {
      return utm.campaign_token.toUpperCase() === tokenOrSource.toUpperCase();
    }
    
    // Check specific source (e.g., 'facebook', 'google', 'tiktok')
    if (tokenOrSource && utm.utm_source !== tokenOrSource) return false;
    
    // Check specific campaign name
    if (campaign && utm.utm_campaign !== campaign) return false;
    
    return true;
  }

  /**
   * Check if user came from specific platform (meta, tiktok, google)
   */
  isFromPlatform(platform) {
    const utm = this.getStoredUTMData();
    if (!utm) return false;
    
    // Check via verified campaign token
    if (utm.campaign_verified && utm.campaign_verified.platform) {
      return utm.campaign_verified.platform === platform.toLowerCase();
    }
    
    // Fallback to utm_source
    const platformMap = {
      'meta': ['facebook', 'instagram', 'meta'],
      'tiktok': ['tiktok', 'tt'],
      'google': ['google', 'google-ads', 'gads'],
    };
    
    return platformMap[platform.toLowerCase()]?.includes(utm.utm_source?.toLowerCase()) || false;
  }

  /**
   * Get campaign token (if exists)
   */
  getCampaignToken() {
    const utm = this.getStoredUTMData();
    return utm?.campaign_token || null;
  }

  /**
   * Check if campaign is verified (valid token)
   */
  isVerifiedCampaign() {
    const utm = this.getStoredUTMData();
    return utm?.campaign_verified !== null && utm?.campaign_verified !== undefined;
  }
}

// Initialize tracker
const utmTracker = new UTMTracker();

// Expose globally for use in theme
window.UTMTracker = utmTracker;

// Example usage in theme:
// {% if request.utm_source %}
//   <!-- Show special content for campaign visitors -->
// {% endif %}

// JavaScript usage:
// if (window.UTMTracker.isFromCampaign('facebook')) {
//   // Show Facebook-specific content
// }

